import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@repo/ui/src/components/ui/button';
import type { ClientActivity } from '@repo/shared-types';
import { ActivityItem } from './ActivityItem';
import { ActivityList } from '../../../components/activity/ActivityItem';
import { useClientActivities } from '../../../hooks/useClients';

interface ActivityTabProps {
  clientId: string;
  initialActivities: ClientActivity[];
}

const PAGE_SIZE = 50;

interface PageState {
  appended: ClientActivity[];
  hasMore: boolean;
  pendingBefore: string | null;
}

export function ActivityTab({ clientId, initialActivities }: ActivityTabProps) {
  const { t } = useTranslation('clients');
  const [pageState, setPageState] = useState<PageState>({
    appended: [],
    hasMore: initialActivities.length === PAGE_SIZE,
    pendingBefore: null,
  });

  const activitiesQuery = useClientActivities(
    pageState.pendingBefore ? clientId : null,
    { limit: PAGE_SIZE, before: pageState.pendingBefore ?? undefined },
  );

  // When a page fetch resolves, append the new batch and reset the trigger.
  // The setState here is intentional — TanStack Query data arrival is the
  // signal we react to; alternative would require a more complex query setup.
  useEffect(() => {
    if (!pageState.pendingBefore || !activitiesQuery.data || activitiesQuery.isFetching) return;
    const batch = activitiesQuery.data;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPageState((prev) => {
      const known = new Set(prev.appended.map((a) => a.id));
      const fresh = batch.filter((a) => !known.has(a.id));
      return {
        appended: fresh.length > 0 ? [...prev.appended, ...fresh] : prev.appended,
        hasMore: batch.length === PAGE_SIZE,
        pendingBefore: null,
      };
    });
  }, [pageState.pendingBefore, activitiesQuery.data, activitiesQuery.isFetching]);

  const all = [...initialActivities, ...pageState.appended];

  const loadMore = () => {
    const last = all[all.length - 1];
    if (last) setPageState((prev) => ({ ...prev, pendingBefore: last.createdAt }));
  };

  return (
    <ActivityList
      count={all.length}
      emptyLabel={t('activity.empty')}
      footer={
        pageState.hasMore ? (
          <div className="border-t border-surface-100 py-3 text-center">
            <Button variant="ghost" size="sm" onClick={loadMore} disabled={activitiesQuery.isFetching}>
              {activitiesQuery.isFetching ? t('activity.loading') : t('activity.loadMore')}
            </Button>
          </div>
        ) : undefined
      }
    >
      {all.map((activity) => (
        <ActivityItem key={activity.id} activity={activity} />
      ))}
    </ActivityList>
  );
}
